import React from 'react';
import {
  TextField,
  Button,
  MenuItem,
  Grid,
  Box,
  Autocomplete,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ROLE } from '../../../_constants/Const';
import { UserSearchParam } from '../../../stores/userManagement/api';
import { useSelector } from 'react-redux';
import { RootState } from '../../../stores/store';

type UserSearchFormProps = {
  params: UserSearchParam;
  updateParams: (params: UserSearchParam) => void;
  handleAction: (action: 'search' | 'clear') => void;
};

const UserSearchForm: React.FC<UserSearchFormProps> = ({
  params,
  updateParams,
  handleAction,
}) => {
  const { t } = useTranslation();

  const { accountsInfoData } = useSelector(
    (state: RootState) => state.accountsInfo,
  );

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    updateParams({
      ...params,
      [name]:
        typeof params[name as keyof UserSearchParam] === 'number'
          ? Number(value)
          : value,
    });
  };

  return (
    <Box sx={{ p: 3, pb: 0, mb: 4 }}>
      <Grid container spacing={2} alignItems="center" justifyContent="center">
        <Grid size={2}>
          <TextField
            fullWidth
            label={t('LBL_USER_ID')}
            name="userId"
            size="small"
            value={params.userId}
            onChange={handleInputChange}
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
          />
        </Grid>
        <Grid size={2}>
          <TextField
            fullWidth
            label={t('LBL_NAME')}
            name="name"
            size="small"
            value={params.name}
            onChange={handleInputChange}
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
          />
        </Grid>
        <Grid size={2}>
          <TextField
            fullWidth
            label={t('LBL_ROLE')}
            name="roleId"
            size="small"
            value={params.roleId}
            select
            onChange={handleInputChange}
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
          >
            <MenuItem value={-1}>&nbsp;{/* 空の選択肢 */}</MenuItem>
            {Object.keys(ROLE).map((key) => (
              <MenuItem key={key} value={Number(key)}>
                {t(ROLE[Number(key)])}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={2}>
          <Autocomplete
            size="small"
            options={accountsInfoData.companyList}
            noOptionsText={t('MSG_NO_OPTION')}
            disableClearable={params.companyKey === ''}
            getOptionLabel={(option) => option.name ?? ''}
            isOptionEqualToValue={(option, value) => option.key === value.key}
            value={
              accountsInfoData.companyList.find(
                (item) => item.key === params.companyKey,
              ) || null
            }
            onChange={(_, newValue) => {
              handleInputChange({
                target: {
                  name: 'companyKey',
                  value: newValue?.key ?? '',
                },
              } as React.ChangeEvent<HTMLInputElement>);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('LBL_COMPANY')}
                fullWidth
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
              />
            )}
          />
        </Grid>

        {/* 検索とクリアボタン */}
        <Grid size={3}>
          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleAction('search')}
              fullWidth
            >
              {t('BTN_SEARCH')}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => handleAction('clear')}
              fullWidth
            >
              {t('BTN_CLEAR')}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UserSearchForm;
